import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import OvLabelledField from '../atoms/OvLabelledField';
import OvOrderEditButton from '../atoms/OvOrderEditButton';
import OvRecordDetailsSection from '../molecules/OvRecordDetailsSection';
import OvEditVideoPlacementDialog from '../molecules/OvEditVideoPlacementDialog';
import OvVideoHealthGoals from '../molecules/OvVideoHealthGoals';
import OvVideoShownIn from '../molecules/OvVideoShownIn';
import { AdminVideo } from '../../../common/model/dto/video/admin-video';
import { VideoUploadItem } from '../../../context/video-upload.types';
import { usePendingReplacement } from '../../../hooks/use-pending-replacement';
import { useVideoPlacements } from '../../../hooks/use-video-placements';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

interface OvVideoShownInSectionProps {
  video: AdminVideo;
  upload?: VideoUploadItem;
  isEditable: boolean;
  onSaved: () => void;
}

const OvVideoShownInSection: FC<OvVideoShownInSectionProps> = ({
  video,
  upload,
  isEditable,
  onSaved,
}) => {
  const { t } = useTranslation();
  const { takesOver: isInherited } = usePendingReplacement(video);
  const placements = useVideoPlacements();
  const [isEditing, setIsEditing] = useState(false);
  const canEdit = isEditable && !isInherited;

  return (
    <OvRecordDetailsSection
      title={t('videos.fields.shownIn')}
      action={
        canEdit && (
          <OvOrderEditButton
            label={t('videos.details.editShownIn')}
            onClick={() => setIsEditing(true)}
          />
        )
      }
    >
      {isInherited ? (
        <Inherited>{t('videos.details.inheritsPlacement')}</Inherited>
      ) : (
        <Fields>
          <OvLabelledField label={t('videos.fields.shownIn')}>
            <OvVideoShownIn
              video={video}
              placements={placements.data}
              upload={upload}
            />
          </OvLabelledField>
          <OvLabelledField label={t('videos.fields.healthGoals')}>
            <OvVideoHealthGoals
              goals={video.health_goals}
              label={t('videos.placement.healthGoalsOf', {
                title: video.title,
              })}
            />
          </OvLabelledField>
        </Fields>
      )}
      {canEdit && (
        <OvEditVideoPlacementDialog
          video={video}
          isOpen={isEditing}
          onClose={() => setIsEditing(false)}
          onSaved={onSaved}
        />
      )}
    </OvRecordDetailsSection>
  );
};

export default OvVideoShownInSection;

const Fields = styled.dl`
  display: flex;
  flex-direction: column;
  gap: ${Variables.spacing.SMALL};
  margin: 0;
`;

const Inherited = styled.p`
  margin: 0;
  color: ${Colours.OV_CHARCOAL};
`;
